import { RawLocation, Route, RouteConfig } from 'vue-router';
import { addFunds } from '@store/modules/add-funds';
import { AddFundsUpgradeRoute } from 'types/add-funds';
import { addFundsUpgradeRouteOrder } from '@src/constants';
import { app } from '@store/modules/app';
import { flattenQueryParam } from '@src/utils/request';
import { investmentEntity } from '@src/store/modules/investment-entity';
import { redirect } from '@src/utils/client';

const getFlowStepIndex = (name: AddFundsUpgradeRoute): number => {
	return addFundsUpgradeRouteOrder.indexOf(name) + 1;
};

const stepConstructor = (route: Route): number => {
	return getFlowStepIndex(route.name as AddFundsUpgradeRoute);
};

const addFundsEntryRedirectHandling = (): string | null => {
	switch (investmentEntity.additionalInvestmentEligibility) {
		case 'ENTITY_NO_INVESTMENTS':
			return '/start-investing';
		case 'ENTITY_NO_PLAN_SELECTION':
			return '/account/ria-upgrade';
		case 'ENTITY_MISSING_INFORMATION':
			switch (investmentEntity.entityType) {
				case 'INDIVIDUAL':
					return `/account/profile/personal-information`;
				case 'JOINT_ACCOUNT':
					return `/account/investment-accounts/joint-account/account-holder-information/edit`;
				case 'TRUST':
					return `/account/investment-accounts/trust/information/edit`;
				case 'LLC':
					return `/account/investment-accounts/entity-account/entity-information/edit`;
			}
	}
	return null;
};

const addFundsOfferingEntryRedirectHandling = (): null | RawLocation => {
	if (investmentEntity.isIraEntity) {
		return {
			name: 'ira-add-funds-start',
			query: { reitOfferingVintageId: addFunds.offeringVintageId },
			replace: true
		};
	}
	switch (investmentEntity.additionalInvestmentEligibility) {
		case 'ENTITY_NO_INVESTMENTS':
			return { name: 'start-investing', replace: true };
		case 'ENTITY_MISSING_INFORMATION':
			switch (investmentEntity.entityType) {
				case 'INDIVIDUAL':
					return {
						name: 'profile-address-edit',
						replace: true
					};
				case 'JOINT_ACCOUNT':
					return {
						name: 'settings-joint-account-information-edit',
						replace: true
					};
				case 'TRUST':
					return {
						name: 'settings-trust-information-edit',
						replace: true
					};
				case 'LLC':
					return {
						name: 'settings-entity-account-information-edit',
						replace: true
					};
			}
	}
	return null;
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const addFundsRoutes: Array<RouteConfig> = [
	{
		path: `/account/repeat-investment/ria`,
		name: 'add-funds-ria',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () =>
			import(/* webpackChunkName: "add-funds-ria" */ '@views/account/add-funds/ria/add-funds-ria.vue'),
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.additionalInvestmentEligibility !== 'VALID' || investmentEntity.entityType === 'IRA') {
				const addFundsSpecialRedirect = addFundsEntryRedirectHandling();
				if (addFundsSpecialRedirect) {
					redirect(addFundsSpecialRedirect);
				} else {
					next({ name: 'account-overview', replace: true });
					app.ADD_TOAST({
						type: 'error',
						message: 'The current account is not eligible for an additional investment at this time.'
					});
				}
			} else {
				if (addFunds.orderComplete) {
					addFunds.clearPreviousOrder();
				}
				if (!addFunds.riaDataLoaded) {
					await addFunds.fetchAndStoreRepeatRiaInvestmentMetadata();
				}
				addFunds.UPDATE_ADD_FUNDS_FLOW('RIA');
				next();
			}
		},
		redirect: { name: 'add-funds-ria-amount' },
		children: [
			{
				path: 'amount',
				name: 'add-funds-ria-amount',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const defaultAmountParam = flattenQueryParam(to.query.defaultAmount);
					addFunds.parseDefaultAmountParam(defaultAmountParam);
					addFunds.UPDATE_INVEST_TAB_ADD_FUNDS(false);
					next();
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-amount" */ '@views/account/add-funds/ria/add-funds-ria-amount.vue'
					)
			},
			{
				path: 'agreements',
				name: 'add-funds-ria-agreements',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (!addFunds.amount || !addFunds.paymentMethodId || !addFunds.reitInvestmentOrderGroupId) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-agreements" */ '@views/account/add-funds/ria/add-funds-ria-agreements.vue'
					)
			},
			{
				path: 'review',
				name: 'add-funds-ria-review',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (
						!addFunds.amount ||
						!addFunds.reitInvestmentOrderGroupId ||
						addFunds.reitDocumentRevisionIds.length < 1
					) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-review" */ '@views/account/add-funds/ria/add-funds-ria-review.vue'
					)
			},
			{
				path: 'success',
				name: 'add-funds-ria-success',
				meta: { step: stepConstructor, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (!addFunds.reitInvestmentOrderGroupId) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-ria-success" */ '@views/account/add-funds/ria/add-funds-ria-success.vue'
					)
			}
		]
	},
	{
		path: `/account/repeat-investment/offering/plaid-redirect`,
		name: `add-funds-offering-plaid-redirect`,
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			await addFunds.fetchAndUpdatePriorOfferingSelections();
			next({
				path: `/account/repeat-investment/offering/${addFunds.offeringVintageId}`,
				query: { oauth_state_id: to.query.oauth_state_id }
			});
		}
	},
	{
		path: `/reits/checkout/repeat/start`,
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			const reitOfferingVintageId = flattenQueryParam(to.query.reitOfferingVintageId);
			if (!reitOfferingVintageId) {
				next({ name: 'account-overview', replace: true });
			} else {
				next({
					name: 'add-funds-offering',
					params: { offeringVintageId: reitOfferingVintageId },
					query: { defaultAmount: to.query.amount }
				});
			}
		}
	},
	{
		path: `/account/repeat-investment/offering/:offeringVintageId`,
		name: 'add-funds-offering',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () =>
			import(/* webpackChunkName: "add-funds-ria" */ '@views/account/add-funds/offering/add-funds-offering.vue'),
		beforeEnter: async (to, from, next) => {
			if (addFunds.orderComplete) {
				addFunds.clearPreviousOrder();
			}
			if (!addFunds.offeringDataLoaded || to.params.offeringVintageId !== addFunds.offeringVintageId) {
				const offeringVintageId = to.params.offeringVintageId;

				addFunds.UPDATE_OFFERING_VINTAGE_ID(offeringVintageId);
				try {
					await addFunds.fetchAndStoreRepeatOfferingInvestmentMetadata(offeringVintageId);
				} catch {}
			}

			if (!addFunds.eligibleToInvestInOffering || investmentEntity.isIraEntity) {
				const addFundsSpecialRedirect = addFundsOfferingEntryRedirectHandling();
				if (addFundsSpecialRedirect) {
					next(addFundsSpecialRedirect);
				} else if (addFunds.offeringType === 'FUNDRISE_IPO') {
					next({ name: 'account-overview', replace: true });
					app.ADD_TOAST({
						type: 'error',
						message: `Thank you for your investment in the Fundrise iPO. You've already invested the maximum amount available to you.`
					});
				} else {
					next({ name: 'account-overview', replace: true });
					app.ADD_TOAST({
						type: 'error',
						message: 'You are not eligible for direct investment in the selected offering at this time.'
					});
				}
			} else {
				addFunds.UPDATE_ADD_FUNDS_FLOW('EDIRECT');
				next();
			}
		},
		redirect: { name: 'add-funds-offering-amount' },
		children: [
			{
				path: 'amount',
				name: 'add-funds-offering-amount',
				meta: { step: 0, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const defaultAmountParam = flattenQueryParam(to.query.defaultAmount);
					addFunds.parseDefaultAmountParam(defaultAmountParam);
					next();
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-offering-amount" */ '@views/account/add-funds/offering/add-funds-offering-amount.vue'
					)
			},
			{
				path: 'agreements',
				name: 'add-funds-offering-agreements',
				meta: { step: 1, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (!addFunds.amount || !addFunds.paymentMethodId || !addFunds.reitInvestmentOrderGroupId) {
						next({
							name: 'add-funds-offering-amount',
							params: { offeringVintageId: addFunds.offeringVintageId ?? '' },
							replace: true
						});
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-offering-agreements" */ '@views/account/add-funds/offering/add-funds-offering-agreements.vue'
					)
			},
			{
				path: 'review',
				name: 'add-funds-offering-review',
				meta: { step: 2, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (
						!addFunds.amount ||
						!addFunds.paymentMethodId ||
						!addFunds.reitInvestmentOrderGroupId ||
						addFunds.reitDocumentRevisionIds.length < 1
					) {
						next({
							name: 'add-funds-offering-amount',
							params: { offeringVintageId: addFunds.offeringVintageId ?? '' },
							replace: true
						});
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-offering-review" */ '@views/account/add-funds/offering/add-funds-offering-review.vue'
					)
			},
			{
				path: 'success',
				name: 'add-funds-offering-success',
				meta: { step: 3, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (!addFunds.reitInvestmentOrderGroupId) {
						next({
							name: 'add-funds-offering-amount',
							params: { offeringVintageId: addFunds.offeringVintageId ?? '' },
							replace: true
						});
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "add-funds-offering-success" */ '@views/account/add-funds/offering/add-funds-offering-success.vue'
					)
			}
		]
	}
];

export default addFundsRoutes;
